import ValidateModule from "./ValidateModule";

import type { IEventChannel, ApiOpts } from "../model/Models";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type CacheModule from "./CacheModule";

const es = global.es;

/**
 * @class WebhookModule
 */
export default class WebhookModule {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private resourceName: string;
    private cache: CacheModule;

    /**
     * 
     * @param eventChannel 
     * @param rqBroker 
     * @param resourceName 
     * @param cache 
     */
    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;

        // TODO FIXME Does this even make sense nowadays?
        eventChannel.addListener("onDestroy", () => {
            // prevent memory leaks when the map widget is destroyed
            this.eventChannel = this.rqBroker = this.resourceName = this.cache = null;
        });

        this.getLogs = this.getLogs.bind(this);
        this.get = this.get.bind(this);
        this.getByIds = this.getByIds.bind(this);
        this.list = this.list.bind(this);
        this.count = this.count.bind(this);
        this.create = this.create.bind(this);
        this.update = this.update.bind(this);
        this.remove = this.remove.bind(this);
    }

    /**
     * 
     * @param id 
     * @param opts 
     * 
     * @returns 
     */
    getLogs(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/" + id + "/log";
        return this.rqBroker.doGet({url: url, opts: opts}); // no-cache
    }

    /**
     * Get the resource from the server
     * 
     * @param id 
     * @param opts 
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    get(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/" + id;
        return this.cache.doGet(this.rqBroker, this.resourceName, url, id, opts);
    }
    
    /**
     * Get the resource from the server
     * 
     * @param ids
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    getByIds(ids: number[], opts?: ApiOpts) {
        if (!ValidateModule.checkIds(ids)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/ids?ids=" + es.util.url.stringify(ids);

        return this.cache.doGet(this.rqBroker, this.resourceName, url, ids, opts);
    }
    
    /**
     * Get the resource from the server
     * 
     * @param criteria
     * @param sortOrder
     * @param offset
     * @param length
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    list(criteria?: object, sortOrder?: object, offset?: number, length?: number, opts?: ApiOpts) {
        //return rqBroker.doGet({url: es.api.getListQueryURL(resourceName, criteria, sortOrder, offset, length)});
        return this.cache.doGetList(this.rqBroker, this.resourceName, criteria, sortOrder, offset, length, opts);
    }
    
    /**
     * Get a count of resources matching the given Criteria
     * 
     * @param criteria
     * @param opts
     * 
     * @returns long
     */
    count(criteria?: object, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/count" + (criteria ? "?criteria=" + es.util.url.stringify(criteria) : "");

        return this.rqBroker.doGet({url: url, opts: opts});
    }
    
    /**
     * POST a new resource to the server (note: id attrib must be -1)
     * 
     * @param resourceObj e.g. 
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    create(resourceObj: object, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(resourceObj)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName);

        return this.rqBroker.doPost({
            url: url,
            opts: opts,
            data: resourceObj
        }).done((rs) => {
            this.cache?.setEntity(this.resourceName, rs);
        });
    }
    
    /**
     * PUT update a resource (note: id attrib must be valid)
     * 
     * @param resourceObj e.g. 
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    update(resourceObj: object, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(resourceObj)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/" + resourceObj.id;

        return this.rqBroker.doPut({
            url: url,
            data: resourceObj,
            opts: opts
        }).done((rs) => {
            this.cache?.setEntity(this.resourceName, rs);
        });
    }
    
    /**
     * Get the resource from the server
     * 
     * @param id 
     * @param opts 
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    remove(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/" + id;

        return this.rqBroker.doDelete({url: url, opts: opts}).done((rs) => {
            this.cache?.clear(this.resourceName, id);
        });
    };
}
